import React, { useEffect, useState } from 'react';
import { adminAPI } from '@/services/api';
import { Building2, Crown, Users, ShieldOff, ShieldCheck, Check, X, ChevronDown, ChevronRight, AlertTriangle, BadgeCheck, ShieldAlert } from 'lucide-react';

const STATUS = {
  active: { label: 'Active', cls: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10' },
  pending_admin: { label: 'Pending Numuv', cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10' },
  pending_head: { label: 'Pending head', cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10' },
  revoked: { label: 'Revoked', cls: 'text-red-600 dark:text-red-400 bg-red-500/10' },
  rejected: { label: 'Rejected', cls: 'text-red-600 dark:text-red-400 bg-red-500/10' },
};

function Pill({ status }) {
  const s = STATUS[status] || { label: status, cls: 'text-slate-500 dark:text-zinc-400 bg-zinc-500/10' };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
}

function EmailVerifiedTag({ verified }) {
  return verified ? (
    <span title="Email verified" data-testid="head-email-verified-badge" className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
      <BadgeCheck className="w-3 h-3" /> Verified
    </span>
  ) : (
    <span title="Email not verified" data-testid="head-email-unverified-badge" className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
      <ShieldAlert className="w-3 h-3" /> Unverified
    </span>
  );
}

export default function AdminOrgHierarchy() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getOrgHierarchy();
      setOrgs(r.organizations || []);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || e.message });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3500); };

  const handle = async (fn, success) => {
    try { await fn(); flash('success', success); load(); }
    catch (e) { flash('error', e.response?.data?.detail || e.message); }
  };

  const confirmThen = (msg, fn) => { if (window.confirm(msg)) fn(); };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="text-slate-500 dark:text-zinc-400 text-sm" data-testid="admin-orgs-loading">Loading organizations…</div>;

  return (
    <div data-testid="admin-org-hierarchy">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight flex items-center gap-2"><Building2 className="w-5 h-5" /> Organizations</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm">{orgs.length} organisation{orgs.length !== 1 && 's'} · Approve heads, revoke domains, manage individual recruiters.</p>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${msg.type === 'error' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`} data-testid="admin-org-msg">{msg.text}</div>
      )}

      <div className="space-y-3">
        {orgs.map(org => {
          const isExp = expanded[org.id];
          const revoked = org.domain_status === 'revoked';
          const headPending = org.head?.member_status === 'pending_admin';
          return (
            <div key={org.id} className={`bg-white dark:bg-[#111114] border rounded-lg overflow-hidden ${revoked ? 'border-red-500/30' : 'border-slate-200 dark:border-zinc-800/50'}`} data-testid={`org-row-${org.id}`}>
              <div className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => toggle(org.id)} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid={`expand-${org.id}`}>
                    {isExp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-900 dark:text-white font-medium">{org.name || '(unnamed)'}</span>
                      <span className="text-slate-500 dark:text-zinc-500 text-xs">@{org.domain || '—'}</span>
                      {revoked && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 font-medium">Domain Revoked</span>}
                      {!revoked && !org.org_approved && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">Pending Approval</span>}
                    </div>
                    <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">
                      <Users className="w-3 h-3 inline mr-1" />
                      {org.total_members} member{org.total_members !== 1 && 's'} · Head: {org.head ? <span className="text-slate-600 dark:text-zinc-300">{org.head.email}</span> : <span className="text-amber-600 dark:text-amber-400">not set</span>}
                      {org.head && <span className="ml-1.5"><EmailVerifiedTag verified={org.head.is_email_verified} /></span>}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {!revoked && !org.org_approved && (
                    <button onClick={() => handle(() => import('@/services/api').then(m => m.adminAPI.approveOrg(org.id, true)), 'Organization approved')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`approve-org-${org.id}`}><Check className="w-3 h-3" /> Approve org</button>
                  )}
                  {!revoked ? (
                    <button onClick={() => confirmThen(`Revoke entire domain @${org.domain}? This blocks ALL members.`, () => handle(() => adminAPI.revokeOrgDomain(org.id), 'Domain access revoked'))} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-medium" data-testid={`revoke-domain-${org.id}`}><AlertTriangle className="w-3 h-3" /> Revoke domain</button>
                  ) : (
                    <button onClick={() => handle(() => adminAPI.restoreOrgDomain(org.id), 'Domain restored')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`restore-domain-${org.id}`}><ShieldCheck className="w-3 h-3" /> Restore domain</button>
                  )}
                </div>
              </div>

              {isExp && (
                <div className="border-t border-slate-200 dark:border-zinc-800/50 p-4 space-y-2 bg-slate-50 dark:bg-[#0a0a0b]" data-testid={`org-detail-${org.id}`}>
                  {/* Head */}
                  {org.head ? (
                    <div className="bg-white dark:bg-[#111114] border border-amber-500/20 rounded-lg p-3 flex items-center justify-between" data-testid={`head-row-${org.id}`}>
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <div>
                          <p className="text-slate-900 dark:text-white text-sm font-medium">{org.head.name || org.head.email.split('@')[0]} · <span className="text-slate-500 dark:text-zinc-500 text-xs">{org.head.email}</span></p>
                          <p className="text-slate-500 dark:text-zinc-500 text-xs flex items-center gap-1.5">{org.head.position} · Recruiter Head <EmailVerifiedTag verified={org.head.is_email_verified} /></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Pill status={org.head.member_status} />
                        {org.head.member_status === 'pending_admin' && (
                          <button onClick={() => handle(() => adminAPI.approveRecruiter(org.head.user_id), 'Recruiter approved')} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`approve-recruiter-${org.head.user_id}`}>Approve</button>
                        )}
                        {org.head.member_status === 'active' && (
                          <button onClick={() => confirmThen('Revoke this recruiter head?', () => handle(() => adminAPI.revokeRecruiter(org.head.user_id), 'Recruiter revoked'))} className="px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-medium" data-testid={`revoke-recruiter-${org.head.user_id}`}>Revoke</button>
                        )}
                        {org.head.member_status === 'revoked' && (
                          <button onClick={() => handle(() => adminAPI.restoreRecruiter(org.head.user_id), 'Recruiter restored')} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`restore-recruiter-${org.head.user_id}`}>Restore</button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-500 dark:text-zinc-500 text-xs p-3 italic">No recruiter head set.</div>
                  )}

                  {/* Subordinates */}
                  {org.subordinates.length > 0 && (
                    <div>
                      <p className="text-slate-500 dark:text-zinc-500 text-[10px] uppercase tracking-wider mt-3 mb-1">Subordinates · {org.subordinates.length}</p>
                      {org.subordinates.map(s => (
                        <div key={s.user_id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex items-center justify-between mt-1" data-testid={`subord-row-${s.user_id}`}>
                          <div>
                            <p className="text-slate-900 dark:text-white text-sm">{s.name || s.email.split('@')[0]} · <span className="text-slate-500 dark:text-zinc-500 text-xs">{s.email}</span></p>
                            <p className="text-slate-500 dark:text-zinc-500 text-xs flex items-center gap-1.5">{s.position} <EmailVerifiedTag verified={s.is_email_verified} /></p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Pill status={s.member_status} />
                            {s.member_status === 'active' && org.head && s.user_id !== org.head.user_id && (
                              <button
                                onClick={() => confirmThen(`Make ${s.name || s.email} the new head of ${org.name}? ${org.head?.email || 'the current head'} will become a subordinate.`, () => handle(() => adminAPI.setOrgHead(org.id, s.user_id), 'Head updated'))}
                                className="px-3 py-1.5 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 rounded-lg text-xs font-medium"
                                data-testid={`set-head-${s.user_id}`}
                                title="Promote to Recruiter Head. Current head becomes a subordinate."
                              >
                                <Crown className="w-3 h-3 inline mr-1" /> Set as head
                              </button>
                            )}
                            {s.member_status === 'active' && (
                              <button onClick={() => confirmThen('Revoke this recruiter?', () => handle(() => adminAPI.revokeRecruiter(s.user_id), 'Recruiter revoked'))} className="px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-medium" data-testid={`revoke-recruiter-${s.user_id}`}><ShieldOff className="w-3 h-3 inline mr-1" />Revoke</button>
                            )}
                            {s.member_status === 'revoked' && (
                              <button onClick={() => handle(() => adminAPI.restoreRecruiter(s.user_id), 'Recruiter restored')} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`restore-recruiter-${s.user_id}`}><ShieldCheck className="w-3 h-3 inline mr-1" />Restore</button>
                            )}
                            {(s.member_status === 'pending_head' || s.member_status === 'pending_admin') && (
                              <button onClick={() => handle(() => adminAPI.approveRecruiter(s.user_id), 'Recruiter approved')} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`approve-recruiter-${s.user_id}`}><Check className="w-3 h-3 inline mr-1" />Approve</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
